import type { Session } from "./types";
import { FROE, PRIVAT, PRIVAT_ID } from "../config/production";
import { krevInnlogging, supabase } from "./supabase";
import { velgLogo } from "@infoflyt/shared/logo";
import * as logolager from "./logolager";

/**
 * Oekten appen jobber i: produksjonen, medlemmet og skiftet.
 *
 * SOEMMEN MOT INNLOGGING. Alt annet leser oekten herfra, ikke fra en fil.
 * Naar Supabase og auth kommer, byttes `hentOekt` ut og resten staar.
 *
 * Lesingen er SYNKRON med vilje, og det er ikke en snarvei. Appen maa kunne
 * starte uten nett — regi staar i kjeller og skog — saa oppstarten kan aldri
 * vente paa et svar fra en server. Siste kjente oekt ligger lokalt og brukes
 * med én gang; en innlogging blir en OPPDATERING av den, ikke en forutsetning
 * for den. Det er samme moenster som paletten og tidskodebryteren.
 */

const NOKKEL = "infoflyt.oekt.v1";

/**
 * Sann naar produksjonen finnes i databasen.
 *
 * «Privat» er arbeid UTENFOR en produksjon — en VJ, en liten jobb, eller
 * noen som bare vil ha tidskodet logg for seg selv. Den har ingen rad noe
 * sted, og `PRIVAT_ID` er ikke en UUID.
 *
 * Uten denne vakta spurte appen Supabase med «privat» som `production_id`
 * saa snart man byttet dit: tre 400-svar i sekundet etter hver oppdatering,
 * «invalid input syntax for type uuid». Og med gjentakelsen i
 * `state/levende.ts` ville de kommet igjen hvert tjuende sekund, for alltid.
 *
 * Det er ogsaa det riktige svaret og ikke bare det billigste: en privat logg
 * skal ikke sendes noe sted.
 */
export function erEkteProduksjon(produksjonId: string): boolean {
  return produksjonId !== "" && produksjonId !== PRIVAT_ID;
}

/**
 * Froe-produksjonen, som med innlogging paa ikke finnes.
 *
 * ========  DEN KOM TILBAKE TIL EN FREMMED  ========
 *
 * Brukeren 02.11.2026: «Naar Christina ogsaa lagde seg en bruker, saa fikk
 * hun opp Øya.» Produksjonen ble slettet fra basen 30.10.2026 — og laa
 * likevel i appen hennes, med Kari Nordmann som medlem og opptaksdag 7,
 * fordi `hentOekt` falt tilbake paa froeet naar lageret var tomt.
 *
 * `lesListe` fikk vakta 31.10.2026, etter samme feil i velgeren. Den ble
 * ikke satt HER, og det var nok: en fersk telefon har ingen lagret oekt.
 *
 * MED INNLOGGING AV staar froeet som foer. Det er stillaset flatene proeves
 * paa, og uten det finnes ingen produksjon i det hele tatt.
 */
export function erFroeproduksjon(produksjonId: string): boolean {
  return krevInnlogging && produksjonId === FROE.production.id;
}

/** Siste kjente oekt. Froeet brukes til det finnes en ekte. */
export function hentOekt(): Session {
  try {
    const raw = localStorage.getItem(NOKKEL);
    if (raw !== null) {
      const s = JSON.parse(raw) as Session;
      if (typeof s.production?.id === "string" && typeof s.member?.id === "string"
        && !erFroeproduksjon(s.production.id)) return s;
    }
  } catch {
    // Ugyldig eller utilgjengelig lagring. Da gjelder froeet.
  }
  /* Med innlogging paa finnes ingen froe-produksjon. Privat finnes alltid:
     appen skal kunne logge foer noen har invitert deg noe sted. */
  return krevInnlogging ? PRIVAT : FROE;
}

/**
 * Skrives naar en innlogging eller et sideskifte gir en ny oekt.
 *
 * Finnes allerede naa, slik at den som bygger innloggingen ikke trenger aa
 * roere noe annet enn kallstedet.
 */
export function settOekt(s: Session): void {
  try {
    localStorage.setItem(NOKKEL, JSON.stringify(s));
  } catch {
    // Privat modus. Oekten lever da bare i minnet, og det er riktig.
  }
}

/* ---- flere produksjoner -------------------------------------------------
 *
 * En frilanser kan ha dagjobb paa én produksjon og kveldsjobb paa en annen.
 * `Session` holder fortsatt ÉN aktiv produksjon og ett medlemskap, slik at
 * ingenting nedstroems merker at det finnes flere — lista ligger ved siden
 * av, ikke inni.
 *
 * Lista leses synkront av samme grunn som oekten: appen maa starte uten
 * nett. Siste kjente liste gjelder til en innlogging oppdaterer den.
 */

const LISTE_NOKKEL = "infoflyt.produksjoner.v1";

/**
 * Det som ligger i lageret. Ugyldige rader forkastes.
 *
 * Er lageret TOMT eller uleselig, er froe-produksjonen den eneste som er
 * kjent, og den maa med. Ellers mister en telefon som staar i privat veien
 * tilbake — og det er nettopp det som skjedde: lista ble aldri skrevet, saa
 * byttet til privat etterlot et tomt lager og ingen produksjon aa gaa til.
 *
 * Dette er ogsaa reparasjonen for telefonene som alt er i den tilstanden.
 * Den forsvinner sammen med FROE naar innloggingen kommer, og
 * `oppdaterOekt()` skriver lista fra medlemskapene i stedet. Se
 * config/production.ts.
 */
function lesListe(): Session[] {
  /*
   * MED INNLOGGING PAA FINNES INGEN FROE-PRODUKSJON.
   *
   * Fallbacken under var riktig da flatene ble proevd uten aa logge inn.
   * Med innlogging paa er den feil, og den var synlig feil 31.10.2026:
   * den inviterte logget inn i appen, hadde ingen medlemskap ennaa — og
   * saa «Øya», en produksjon som var slettet fra basen for to dager
   * siden. Et tomt lager betyr tomt naar serveren er fasiten.
   */
  const reserve = krevInnlogging ? [] : [FROE];
  try {
    const raw = localStorage.getItem(LISTE_NOKKEL);
    if (raw === null) return reserve;
    const lest = JSON.parse(raw) as Session[];
    if (!Array.isArray(lest)) return reserve;
    const gyldige = lest.filter((s) =>
      typeof s?.production?.id === "string" && typeof s?.member?.id === "string"
      /* Og froeet, om det ligger i en liste som ble skrevet foer vakta. */
      && !erFroeproduksjon(s.production.id));
    return gyldige.length > 0 ? gyldige : reserve;
  } catch {
    // Utilgjengelig lagring. Froeet er da det eneste vi vet om.
    return reserve;
  }
}

/**
 * Produksjonene som er kjent paa denne telefonen, med «privat» sist.
 *
 * Den aktive oekten ERSTATTER sin egen rad i lista. Den har de ferskeste
 * verdiene — opptaksdag og enhet endres mens man jobber — og lagret
 * rekkefoelge beholdes, slik at velgeren ikke stokker om naar man bytter.
 *
 * Privat er alltid med og kan ikke fjernes. Den er stedet loggen ligger naar
 * man jobber utenfor en produksjon, og feltappen skal kunne brukes uten at
 * noen har invitert deg noe sted.
 */
export function hentProduksjoner(): Session[] {
  const aktiv = hentOekt();
  const lagret = lesListe();
  const fletta = lagret.some((s) => s.production.id === aktiv.production.id)
    ? lagret.map((s) => (s.production.id === aktiv.production.id ? aktiv : s))
    : [...lagret, aktiv];

  // Privat sist, uansett hvor den ligger i lageret.
  const ekte = fletta.filter((s) => s.production.id !== PRIVAT_ID);
  const privat = fletta.find((s) => s.production.id === PRIVAT_ID) ?? PRIVAT;
  return [...ekte, privat];
}

/**
 * Opptaksdagen for en produksjon.
 *
 * Beholder den regi staar paa saa lenge den finnes i produksjonen — man
 * skal kunne bla til en annen dag og bli der. Finnes den ikke, er den
 * ikke riktig uansett hva den har vaert, og dagen som gjelder naa
 * gjelder. Uten opptaksdager i det hele tatt staar «1»: en produksjon
 * som ikke er rigget har ingen dag aa arve fra noen andre.
 */
function dagFor(
  post: {
    alle: Set<string>; naa: string; idag: string | null; vindu: string | null;
  } | undefined,
  staaende: string | undefined,
): string {
  if (post === undefined) return staaende ?? "1";
  /* Er vi INNE i en opptaksdag, er det den. Den slaar ogsaa datoen:
     klokka 01:00 hoerer til gaarsdagens dag. */
  if (post.vindu !== null) return post.vindu;
  /*
   * DAGENS DAG SLAAR ALT.
   *
   * Brukeren 02.11.2026: «Hvis 2 november er opptaksdag 2, saa maa
   * opptaksdag 2 komme opp naar du aapner appen 2 november.» Det er
   * sterkere enn aa beholde dagen man sto paa, og det er riktig: aapner
   * man appen paa sett, er det dagen man staar i man skal logge paa.
   * Blar man til en annen dag inne i loggen, er det en VISNING — det
   * flytter ikke oekten.
   */
  if (post.idag !== null) return post.idag;
  if (staaende !== undefined && post.alle.has(staaende)) return staaende;
  return post.naa;
}

/**
 * Skriver ned hele lista, PRIVAT INKLUDERT.
 *
 * Privat ble foer filtrert bort her, for aa holde den kanoniske verdien ett
 * sted. Men da forsvant opptaksdagen man hadde satt paa den private loggen
 * hver gang man forlot den — og det er like mye arbeid som et skift.
 */
export function settProduksjoner(liste: readonly Session[]): void {
  try {
    localStorage.setItem(LISTE_NOKKEL, JSON.stringify(liste));
  } catch {
    // Privat modus. Lista lever da bare i minnet.
  }
}

/**
 * Bytter aktiv produksjon og returnerer den nye oekten.
 *
 * Returnerer null naar id-en ikke finnes i lista — da skal ingenting skje.
 * Et bytte river ned og bygger opp loggtilstanden, og det maa ikke gjoeres
 * paa et oppslag som slo feil. Se SessionProvider.
 */
export function byttProduksjon(productionId: string): Session | null {
  const liste = hentProduksjoner();
  const funnet = liste.find((s) => s.production.id === productionId);
  if (funnet === undefined) return null;

  /*
   * HELE LISTA SKRIVES NED FOER VI FORLATER PRODUKSJONEN.
   *
   * Her laa feilen. Lista ble utledet av den aktive oekten, og
   * `settProduksjoner` ble bare kalt fra `oppdaterOekt()` — som krever
   * innlogging. Med innlogging av ble lageret aldri skrevet, saa en
   * produksjon fanns i lista BARE saa lenge den var den aktive. Byttet til
   * privat overskrev nettopp det, og produksjonen var borte uten vei
   * tilbake.
   *
   * `liste` inneholder den vi staar i naa, med sine ferskeste verdier. Den
   * maa paa disk foer `settOekt` tar over.
   */
  settProduksjoner(liste);
  settOekt(funnet);
  return funnet;
}

export function glemOekt(): void {
  try {
    localStorage.removeItem(NOKKEL);
  } catch {
    // Ingenting aa gjoere.
  }
}

/* ---- innlogging ---------------------------------------------------------
 *
 * Finnes det ingen klient, finnes det ingen innlogging. Da er `innlogget()`
 * sann og froeet gjelder — appen oppfoerer seg som foer.
 */

/** Sann naar vi har en oekt aa jobbe i, enten fra auth eller fra froeet. */
export async function innlogget(): Promise<boolean> {
  if (!krevInnlogging || supabase === null) return true;
  const { data } = await supabase.auth.getSession();
  return data.session !== null;
}

/*
 * «Finnes ikke» ER IKKE EN FEIL VI VISER.
 *
 * Med selvregistrering av svarer serveren 422 `otp_disabled` paa en adresse
 * som ikke har konto — «Signups not allowed for otp». Viste vi den, hadde
 * flata fortalt en fremmed hvem som jobber paa produksjonen, og hele poenget
 * med det felles svaret var borte.
 *
 * Derfor behandles de to kodene som suksess: brukeren kommer til kodefeltet,
 * og der feiler en kode som ikke finnes paa vanlig maate. Verifisert mot det
 * levende prosjektet 26.10.2026.
 */
const TAUSE = new Set(["otp_disabled", "signup_disabled", "email_not_confirmed"]);

function erTaus(kode: string | undefined): boolean {
  return kode !== undefined && TAUSE.has(kode);
}

/**
 * Sender engangskoden. Ingen passord — se docs/02-arkitektur.md.
 *
 * KODE, IKKE LENKE. Dette het `sendLenke` og lovet en lenke, men malen
 * (`supabase/templates/magic_link.html`) har aldri sendt noen: en lenke paa
 * iOS aapner ALLTID i standardnettleseren, og en PWA fra hjemskjermen har
 * eget lager atskilt fra Safari. Oekten ville landet i Safari, ikke i appen,
 * og ingen omdirigering kan rette det. Rettet 26.10.2026 — skjermen ba om en
 * lenke som ikke kom, og ingen kunne logge inn.
 *
 * `shouldCreateUser: false`: kontoen opprettes foerste gang man gaar inn i en
 * produksjon, ikke ved at noen skriver adressen sin i et felt. Selvregistrering
 * er av i prosjektet (`supabase/config.toml`), og dette er det samme kravet
 * fra klientens side. Se docs/09-juridisk.md § «Saerlig om innloggingen».
 *
 * Krever nett. Det er den ENESTE handlingen i appen som gjoer det, og den skjer
 * én gang: oekten lagres lokalt og overlever at appen lukkes, saa regi kan
 * jobbe offline resten av produksjonen.
 */
export async function sendKode(epost: string): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase.auth.signInWithOtp({
    email: epost.trim(),
    options: { shouldCreateUser: false },
  });
  if (error === null || erTaus(error.code)) return { ok: true };
  return { ok: false, feil: error.message };
}

/**
 * Loeser koden inn i en oekt.
 *
 * `type: "email"` godtar koden fra begge slags e-poster; `"magiclink"` bare
 * fra en innloggingslenke. Med én felles mal er det den foerste vi vil ha.
 *
 * Lykkes den, fyrer `onAuthStateChange` i App.tsx og appen bytter flate selv.
 */
export async function bekreftKode(
  epost: string, kode: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase.auth.verifyOtp({
    email: epost.trim(),
    token: kode.replace(/\s/g, ""),
    type: "email",
  });
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

/** Adressen man er logget inn med, eller tom. Vises i menyen. */
export async function minEpost(): Promise<string> {
  if (supabase === null) return "";
  const { data } = await supabase.auth.getUser();
  return data.user?.email ?? "";
}

export async function loggUt(): Promise<void> {
  await supabase?.auth.signOut();
  glemOekt();
}

/**
 * Henter produksjonene og medlemskapene fra serveren og lagrer dem lokalt.
 *
 * Feiler den — offline, eller serveren er nede — beholdes siste kjente oekt
 * og liste. Appen skal aldri staa og vente paa nett for aa kunne logge. Se
 * docs/02-arkitektur.md § Offline.
 */
export async function oppdaterOekt(): Promise<void> {
  if (supabase === null) return;
  const { data: auth } = await supabase.auth.getUser();
  const bruker = auth.user;
  if (bruker == null) return;

  /*
   * INVITASJONENE KREVES FOERST, og det manglet helt.
   *
   * Nettflata har kalt denne ved hver innlogging siden 27.10.2026.
   * Appen gjorde det ikke, og foelgen var stille og alvorlig: en som ble
   * invitert som koordinator eller A-foto kunne logge inn i appen og
   * staa uten medlemskap. Raden laa inert paa adressen, appen spurte
   * aldri om den, og brukeren saa bare «privat» — uten at noe sa fra.
   *
   * Adressen leses av tjeneren fra tokenet, ikke sendt herfra. Se
   * migrasjon 20261027180000.
   */
  const { data: krevd, error: krevfeil } = await supabase.rpc("krev_invitasjoner");
  if (krevfeil !== null) {
    /* Feilen skal ikke kastes. Den laa usynlig i fire dager fordi
       funksjonen ikke var naabar fra APIet — se 20261031120000. */
    console.warn("[infoflyt] krev_invitasjoner feilet:", krevfeil.message);
  }
  const bandt = typeof krevd === "number" ? krevd : 0;

  /*
   * ALLE medlemskap, ikke bare ett.
   *
   * Her sto `.limit(1).maybeSingle()`, og da fantes bare den produksjonen
   * serveren tilfeldigvis returnerte foerst. En frilanser paa tre
   * produksjoner saa én av dem, og kunne ikke bytte til de andre.
   */
  const { data, error } = await supabase
    .from("production_member")
    .select(`
      id, display_name, initials, role, unit_id, extra_roles,
      unit:unit_id ( name ),
      production:production_id (
        id, name, framerate, drop_frame, end_date, nle,
        logo_path, company:company ( logo_path )
      )
    `)
    .eq("user_id", bruker.id);

  if (error !== null || data === null) return;

  const forrige = hentOekt();
  const kjente = hentProduksjoner();
  const liste: Session[] = [];

  /*
   * ========  OPPTAKSDAGEN HOERER TIL PRODUKSJONEN  ========
   *
   * Her sto `fra?.shootDay ?? forrige.shootDay`, og det andre leddet var
   * feilen: en produksjon appen ikke kjente fra foer ARVET dagen fra den
   * man tilfeldigvis sto i. Froeet for «Oeya» staar paa dag 7, saa en ny
   * produksjon ble logget paa dag 7 — mens nettflata leste dag 1.
   *
   * Samme produksjon, samme post, to ulike dager: loggen fra appen dukket
   * aldri opp paa nettflata, og ingen av flatene sa noe galt. Brukeren
   * 01.-02.11.2026: «Jeg faar fortsatt ikke opp loggen under logg i
   * nettflaten naar jeg skriver i appen.»
   *
   * Dagen leses derfor fra produksjonens egne opptaksdager — den samme
   * tabellen nettflata slaar opp i. En dag regi har bladd til beholdes,
   * men bare hvis den FINNES i produksjonen; en dag som ikke finnes kan
   * ikke vaere riktig.
   */
  const iDag = new Date().toISOString().slice(0, 10);
  const naaMs = Date.now();
  const dager = new Map<
    string,
    { alle: Set<string>; naa: string; idag: string | null; vindu: string | null }>();
  {
    const ider = (data as readonly { production: { id?: string } | null }[])
      .map((r) => r.production?.id)
      .filter((x): x is string => typeof x === "string");
    if (ider.length > 0) {
      const { data: rader } = await supabase
        .from("shoot_day")
        .select("production_id, day, date, starts_at, ends_at")
        .in("production_id", ider)
        .order("date", { ascending: true });
      for (const r of (rader ?? []) as readonly Record<string, unknown>[]) {
        const pid = String(r["production_id"]);
        const dag = String(r["day"]);
        const post = dager.get(pid)
          ?? { alle: new Set<string>(), naa: dag, idag: null, vindu: null };
        post.alle.add(dag);
        /* Dagen som gjelder NAA: den siste som har begynt. Er ingen
           begynt, staar den foerste — produksjonen har ikke startet, og
           dag 1 er da riktigere enn ingenting. */
        if (String(r["date"]) <= iDag) post.naa = dag;
        /* Og dagen som ER i dag, naar den finnes. */
        if (String(r["date"]) === iDag) post.idag = dag;
        /* VINDUET SLAAR DATOEN. Et nattskift fra 18:00 til 03:30 er én
           opptaksdag med to datoer, og klokka 01:00 er man fortsatt i
           den. Se migrasjon 20261102110000. */
        const fra = r["starts_at"] == null ? null : Date.parse(String(r["starts_at"]));
        const til = r["ends_at"] == null ? null : Date.parse(String(r["ends_at"]));
        if (fra !== null && til !== null && naaMs >= fra && naaMs <= til) {
          post.vindu = dag;
        }
        dager.set(pid, post);
      }
    }
  }

  for (const rad of data) {
    const p = rad.production as unknown as Record<string, unknown> | null;
    if (p === null) continue;
    const id = String(p["id"]);
    // Enheter og opptaksdag hoerer ikke til medlemskapet. De beholdes fra
    // det vi alt vet om produksjonen, saa et oppslag ikke nullstiller dem.
    const fra = kjente.find((k) => k.production.id === id);
    liste.push({
      production: {
        id,
        name: String(p["name"]),
        framerate: String(p["framerate"]) as Session["production"]["framerate"],
        dropFrame: Boolean(p["drop_frame"]),
        endDateIso: String(p["end_date"]),
        nle: p["nle"] === null ? null : String(p["nle"]) as Session["production"]["nle"],
        /* Regelen brukes HER, én gang. Se Production i state/types.ts. */
        logoSti: velgLogo(
          p["logo_path"] === null || p["logo_path"] === undefined
            ? null : String(p["logo_path"]),
          (p["company"] as { logo_path?: string | null } | null)?.logo_path ?? null,
        ),
        units: fra?.production.units ?? [],
      },
      member: {
        id: String(rad.id),
        displayName: String(rad.display_name),
        initials: String(rad.initials),
        role: String(rad.role) as Session["member"]["role"],
        extraRoles: Array.isArray(rad.extra_roles)
          ? (rad.extra_roles as unknown[]).map(String) as Session["member"]["role"][]
          : [],
      },
      shootDay: dagFor(dager.get(id), fra?.shootDay),
      /*
       * TEAMETS NAVN KOMMER MED NED.
       *
       * Her sto bare `fra?.unitName ?? ""` — appen kjente `unit_id`, men
       * aldri navnet, saa «Enhet» i rapportskjemaet sto tomt selv om
       * teamet var satt for lengst. Brukeren 02.11.2026: «man kan velge
       * "enhet", men det er allerede definert, saa dette feltet kan arve
       * den verdien.»
       */
      unitName: ((): string => {
        const u = rad.unit as { name?: string } | null;
        return typeof u?.name === "string" ? u.name : fra?.unitName ?? "";
      })(),
      /* Teamet kommer fra medlemsraden. Det maa finnes OFFLINE, fordi
         oekt-id-en utledes av det — se db.sessionForDay. */
      unitId: rad.unit_id == null ? "" : String(rad.unit_id),
    });
  }

  /*
   * ========  PRODUKSJONER MAN IKKE ER MED I LENGER  ========
   *
   * Brukeren 01.11.2026: «Hvis noen faar sparken, saa maa vi ha
   * muligheten til aa fjerne tilgangen deres.»
   *
   * Serveren nekter fra det sekundet `active_to` er satt — hver eneste
   * hjelpefunksjon i `tilgang` spoer etter den. Men loggen,
   * dokumentene og DELTAKERLISTA ligger ogsaa paa telefonen, og en
   * tilgang som er fjernet alle andre steder er ikke fjernet saa lenge
   * den ligger der.
   *
   * Her samles bare id-ene. Slettingen gjoeres av SessionProvider, som
   * er den som kan naa filene i OPFS — samme vei som sluttdatoen gaar.
   *
   * Svaret er lest UTEN feil (vi gikk ut over), saa en tom liste betyr
   * at man ikke er med noe sted, ikke at nettet svikter.
   */
  const paaServeren = new Set(liste.map((s) => s.production.id));
  const borte = kjente.filter(
    (k) => k.production.id !== PRIVAT_ID && !paaServeren.has(k.production.id));
  mistet = borte.map((k) => ({ id: k.production.id, navn: k.production.name }));

  /*
   * DE BLIR STAAENDE I LISTA, MERKET.
   *
   * Brukeren 01.11.2026: «Behold uleverte poster lokalt og si fra om
   * dem.» En post man ikke kan naa er ikke beholdt, saa produksjonen
   * maa fortsatt kunne velges — men uten tilgang, og uten aa kunne
   * skrive. Har den ingenting ulevert, tar `glemProduksjon` den bort
   * med én gang etterpaa. Se SessionProvider.
   */
  for (const k of borte) {
    liste.push({
      ...k,
      production: { ...k.production, utenTilgang: true },
    });
  }

  /*
   * Privat beholdes. Den finnes ikke paa serveren, og en oppfriskning av
   * medlemskapene skal ikke ta med seg den private loggens opptaksdag.
   */
  const privat = kjente.find((k) => k.production.id === PRIVAT_ID);
  settProduksjoner(privat === undefined ? liste : [...liste, privat]);

  /*
   * Aktiv produksjon beholdes om den fortsatt finnes. Et oppslag skal ikke
   * flytte regi til en annen produksjon midt i et skift.
   */
  /*
   * ========  INVITASJONER SOM NETTOPP BANDT SEG  ========
   *
   * `bandt` er antallet rader `krev_invitasjoner` koblet til kontoen i
   * dette oppslaget. Er det null, er ingenting nytt — og da skal ingen
   * dialog opp, uansett hvor mange produksjoner man har.
   *
   * Det er derfor telleren brukes og ikke bare en diff mot det vi kjente
   * fra for: ved foerste innlogging paa en ny telefon er ALT ukjent, og
   * en diff alene ville meldt hver eneste produksjon som en invitasjon.
   */
  /*
   * ========  EN FAGFUNKSJON MAN HAR FAATT I TILLEGG  ========
   *
   * Prodleder kan gi noen en fagfunksjon til under Oppsett. Det endrer
   * hva man faar gjoere — en koordinator som ogsaa blir A-foto faar
   * jam-knappen — og en tilgang man ikke vet om er en tilgang man ikke
   * bruker. Diffen gaar mot det telefonen alt kjente, saa en ny telefon
   * melder ingenting: da er ALT ukjent, og det er ikke nytt.
   */
  for (const ny of liste) {
    const foer = kjente.find((k) => k.production.id === ny.production.id);
    if (foer === undefined) continue;
    const hadde = new Set([foer.member.role, ...(foer.member.extraRoles ?? [])]);
    const fikk = [ny.member.role, ...(ny.member.extraRoles ?? [])]
      .filter((r) => !hadde.has(r));
    if (fikk.length === 0) continue;
    leggTilUleste(fikk.map((r) => ({
      produksjonId: ny.production.id,
      navn: ny.production.name,
      rolle: r,
      slag: "fagfunksjon" as const,
    })));
  }

  const nyeIder = new Set<string>();
  if (bandt > 0) {
    const kjenteIder = new Set(kjente.map((k) => k.production.id));
    const nye = liste.filter((s) => !kjenteIder.has(s.production.id));
    for (const s of nye) nyeIder.add(s.production.id);
    if (nye.length > 0) {
      leggTilUleste(nye.map((s) => ({
        produksjonId: s.production.id,
        navn: s.production.name,
        rolle: s.member.role,
        slag: "invitasjon" as const,
      })));
    }
  }

  /*
   * ========  EN INVITASJON FLYTTER DEG IKKE AV SEG SELV  ========
   *
   * Her sto `settOekt(liste[0])` naar den forrige produksjonen ikke
   * fantes lenger — og `liste[0]` er ofte nettopp invitasjonen som
   * akkurat bandt seg. Foelgen var at man ble flyttet INN i den foer
   * dialogen rakk aa spoerre, og da spurte den om noe som var avgjort.
   *
   * Nye invitasjoner er derfor ikke kandidater for det automatiske
   * valget. Finnes ingen andre, blir man staaende i privat — og
   * dialogen er ett trykk unna. Det er riktig vei aa ta feil: aa staa
   * for lenge i privat er en irritasjon, mens aa bli kastet inn i en
   * annen produksjon midt i en opptaksdag kan sende en loggpost til
   * feil oekt.
   */
  const fortsatt = liste.find((s) => s.production.id === forrige.production.id);
  if (fortsatt !== undefined) settOekt(fortsatt);
  else if (forrige.production.id !== PRIVAT_ID) {
    const ikkeInvitasjon = liste.find((s) => !nyeIder.has(s.production.id));
    if (ikkeInvitasjon !== undefined) settOekt(ikkeInvitasjon);
    else settOekt(PRIVAT);
  }

  /*
   * LOGOENE HENTES NED HER, fordi her er vi definitivt paa nett — svaret
   * fra serveren ligger i haanden.
   *
   * Alle produksjonene, ikke bare den aktive: en frilanser bytter mellom
   * dem i menyen, og det kan skje uten dekning. Kallet venter vi ikke paa.
   * En logo er pynt; oekten skal vaere framme straks.
   */
  void (async () => {
    for (const s of liste) await logolager.sikre(s.production.logoSti);
    /* Ryddingen kjenner ALLE stiene som gjelder. Se logolager.rydd. */
    await logolager.rydd(liste.map((s) => s.production.logoSti));
  })();
}


/* ---- invitasjoner man ikke har sett ennaa ---------------------------
 *
 * Brukeren 31.10.2026: «Jeg onsker at man skal faa beskjed om det ogsaa
 * naar man logger inn forste gang etter aa bli invitert … Velger du aa
 * fortsette paa den du var paa, saa skal man faa et merke paa antall
 * produksjoner som venter paa deg.»
 *
 * Derfor to tilstander og ikke én: en invitasjon er ULEST til man enten
 * gaar inn i produksjonen eller avviser dialogen for den. Et varsel som
 * bare vises én gang er et varsel man mister mens telefonen ligger i
 * lomma paa vei til sett.
 *
 * Lista ligger i nettleseren og ikke i basen. Den sier ingenting om
 * TILGANG — den er bundet uansett — bare om hva denne telefonen har
 * vist deg.
 */

/**
 * Produksjoner som forsvant ved siste oppslag.
 *
 * Leses én gang og toemmes, som `lesNyeInvitasjoner` paa nettflata:
 * slettingen skal skje én gang, ikke ved hver gjengivelse.
 */
export interface Mistet {
  readonly id: string;
  readonly navn: string;
}

let mistet: readonly Mistet[] = [];

export function lesMistedeProduksjoner(): readonly Mistet[] {
  const m = mistet;
  mistet = [];
  return m;
}

/** Tar produksjonen ut av lista denne telefonen kjenner. */
export function glemProduksjon(produksjonId: string): void {
  settProduksjoner(
    hentProduksjoner().filter((p) => p.production.id !== produksjonId));
}

/**
 * «Du er ikke lenger med i X. N poster ble aldri levert.»
 *
 * Legges i samme koe som invitasjonene: det er den koeen som allerede
 * overlever at appen lukkes, viser én beskjed om gangen og baerer
 * merket i menyen. Her peker merket paa en produksjon man FORTSATT kan
 * gaa inn i — det er nettopp poenget, postene ligger der.
 */
export function meldUlevert(
  produksjonId: string, navn: string, antall: number,
): void {
  leggTilUleste([{
    produksjonId, navn, rolle: "crew", slag: "uleverte", antall,
  }]);
}

const ULESTE = "infoflyt.uleste.v1";

export interface Ulest {
  readonly produksjonId: string;
  readonly navn: string;
  readonly rolle: Session["member"]["role"];
  /**
   * Hva beskjeden gjelder.
   *
   * «invitasjon» er en produksjon du nettopp ble med i. «fagfunksjon»
   * er en rolle du har faatt I TILLEGG i en produksjon du alt er med
   * i — brukeren 01.11.2026: «saa maa brukeren faa en melding om det
   * paa sine flater, slik som invitasjon til en produksjon».
   */
  readonly slag?: "invitasjon" | "fagfunksjon" | "uleverte";
  /** Bare for «uleverte»: hvor mange poster som aldri kom fram. */
  readonly antall?: number;
  /**
   * Om dialogen har vaert oppe for denne.
   *
   * TO TILSTANDER, IKKE EN. «Dialogen er vist» og «jeg har vaert inne i
   * produksjonen» er ikke det samme, og foerst sto de som ett: begge
   * knappene i dialogen kvitterte, saa den som valgte «bli hvor jeg er»
   * mistet merket i samme trykk. Brukeren 01.11.2026 ville ha det
   * motsatte — merket skal staa til man HAR vaert der.
   */
  readonly vist?: boolean;
}

export function hentUleste(): readonly Ulest[] {
  try {
    const raa = localStorage.getItem(ULESTE);
    if (raa === null) return [];
    const lest = JSON.parse(raa) as Ulest[];
    return Array.isArray(lest)
      ? lest.filter((u) => typeof u?.produksjonId === "string") : [];
  } catch { return []; }
}

function skrivUleste(liste: readonly Ulest[]): void {
  try { localStorage.setItem(ULESTE, JSON.stringify(liste)); }
  catch { /* Privat vindu. Da gjelder varselet bare denne oekten. */ }
}

/*
 * Noekkelen er produksjon + rolle, ikke produksjon alene.
 *
 * Med bare produksjonen ville en fagfunksjon man fikk i tillegg blitt
 * slukt av en invitasjon som alt sto der — og omvendt. De er to ulike
 * beskjeder om samme produksjon, og begge skal sies.
 */
function noekkel(u: Ulest): string {
  return `${u.produksjonId}|${u.slag ?? "invitasjon"}|${u.rolle}`;
}

function leggTilUleste(nye: readonly Ulest[]): void {
  const finnes = new Set(hentUleste().map(noekkel));
  const samlet = [...hentUleste(), ...nye.filter((u) => !finnes.has(noekkel(u)))];
  skrivUleste(samlet);
}

/** Kvitterer én. Kalles naar man FAKTISK gaar inn i produksjonen. */
export function kvitterUlest(produksjonId: string): void {
  skrivUleste(hentUleste().filter((u) => u.produksjonId !== produksjonId));
}

/**
 * Dialogen er vist for denne. Merket blir staaende.
 *
 * Uten dette ville dialogen kommet opp paa nytt ved hver innlogging for
 * noe man alt har tatt stilling til — og et varsel man maa avvise to
 * ganger er et varsel man slutter aa lese.
 */
export function merkVist(produksjonId: string, rolle?: string): void {
  skrivUleste(hentUleste().map((u) => (
    u.produksjonId === produksjonId && (rolle === undefined || u.rolle === rolle)
      ? { ...u, vist: true } : u)));
}

/** Produksjoner man er invitert til og ikke har vaert inne i. */
export function erUlest(produksjonId: string): boolean {
  return hentUleste().some((u) => u.produksjonId === produksjonId);
}
